// Stellarium Web - Copyright (c) 2022 - Stellarium Labs SRL
//
// This program is licensed under the terms of the GNU AGPL v3, or
// alternatively under a commercial licence.
//
// The terms of the AGPL v3 license can be found in the main directory of this
// repository.

<template>
  <!-- [guanxing patch] 出生天象首页（M-SW4）：首访全屏覆盖层。星空在背后加载，
       用户填写生日与地点，提交后把引擎冻结在该时刻（time_speed=0）并淡出露出
       天象。URL 带 date 参数直达（分享/刷新重现）时不挂载本层（见 App.vue）。
       [guanxing patch] M-SW21：升级为全屏可滚动落地页——五幕叙事（Hero/以星为尺/
       日历之外/一座天上的宫城/定格）浮在实时星空之上，「星空即 Hero」；提交链路（script）
       与 M-SW4/M-SW15 契约完全不变。（M-SW60：第四幕原为「三问」折叠 FAQ，已整段替换
       为文化段，见下方第四幕注释。） -->
  <div class="birth-overlay">
    <!-- 双层 scrim：纵向渐变（上下深中间透）+ 四角 vignette，星空可见、文字可读 -->
    <div class="landing-scrim" aria-hidden="true"></div>
    <div class="landing-vignette" aria-hidden="true"></div>

    <div class="landing-inner">
      <!-- 第一幕 · Hero -->
      <section class="hero">
        <div class="hero-kicker">星临</div>
        <!-- [guanxing patch] 用户需求（移动端）：主标分三行「宇宙不认日历，/但记得你降临/的那一刻。」
             并居中。居中由 .hero 的 text-align:center 天然保证；第二处换行用移动端专用
             <br class="hero-br-m">（桌面 display:none 隐藏，维持原两行版式；移动端显示）。 -->
        <h1 class="hero-title gx-serif">宇宙不认日历，<br>但记得你降临<br class="hero-br-m">的那一刻。</h1>
        <p class="hero-sub">
          输入你的出生时刻与地点，还原那一瞬间真实的星空与行星排布——一帧不被任何历法改写的宇宙快照。
        </p>
        <!-- [guanxing patch] M-SW40（用户需求「去掉首页上面定格我的星空的按钮」）：Hero
             CTA 按钮整颗删除（连同 scrollToCta 方法与 .hero-cta 样式，不留死代码）。
             Hero 区退化为「小标 + 标题 + 副文案 + 下滚提示」，下滚 cue 仍但是唯一引导：
             落地层本身可滚动，用户向下滚即到达第五幕 CTA 表单，滚动 path 不受影响。 -->
        <div class="hero-scrollcue" aria-hidden="true"></div>
      </section>

      <!-- 第二幕 · 以星为尺 -->
      <section class="land-sec reveal">
        <div class="sec-kicker">壹 · 以星为尺</div>
        <div class="sec-prose">
          <p>时间本身没有刻度。为了在流动的岁月里锚定时节，古人仰望苍穹，把漫天群星划成<span class="gx-gold">三垣二十八宿</span>——星空是人类最早的标尺与记录本。</p>
          <p>今天，我们用同一片星空，标记你抵达宇宙的原点。</p>
        </div>
      </section>

      <!-- 第三幕 · 日历之外
           [guanxing patch] M-SW60（用户需求「把首页三问换一段」+ AskUserQuestion 定案）：
           本幕与「快照不认历法」语义融合改写——开头补「时间本是一条没有刻度的长河」的
           时间主题，原第三段（行星漂移/岁差/快照不变）并入「永远只有一种答案」并接上
           「不占卜、不预测，只把这一帧真实天象还给你」。 -->
      <section class="land-sec reveal">
        <div class="sec-kicker">贰 · 日历之外</div>
        <div class="sec-prose">
          <p>时间本是一条没有刻度的长河。公历以回归年为尺，农历以月相为尺，都是人后来打上去的标签——同一个降生事件，换一套历法，就落成不同的日期。</p>
          <p>但你的出生，是四维时空里<span class="gx-gold">唯一固定的事件</span>。锁定精确的时刻与经纬度，就能还原那一瞬间：出生地头顶的整片星空，与太阳系八大行星真实的排布。</p>
          <p>行星会漂移，恒星会随岁差偏移，而那一刻的星空与行星排布<span class="gx-gold">永远只有一种答案</span>。星临不占卜、不预测，只把这一帧真实天象还给你。</p>
        </div>
      </section>

      <!-- 第四幕 · 一座天上的宫城
           [guanxing patch] M-SW60（用户定案）：原「叁 · 三问」（三个原生 details 折叠
           FAQ：生日随历法改变 / 行星布局不重演 / 岁差）整段替换。换段理由：三问内容与
           前幕「日历之外」重复，而首页缺文化主线——改为三垣二十八宿 + 西方八十八星座的
           文化段，并掺入「古人以星读时间」的时间主题。幕号仍为「叁」，第五幕「肆 · 定格」
           编号不变（避免连带重排）。原 .faq / .faq-no / .faq-body 样式已无引用，按决策人
           选择保留未清理。（M-SW61：按决策人指令删去末段「星临两套都留着……不是签文」，
           该幕现为两段。） -->
      <section class="land-sec reveal">
        <div class="sec-kicker">叁 · 一座天上的宫城</div>
        <div class="sec-prose">
          <p>同一片天，东方与西方各有一套认星的方式。西方把它画成英雄与神兽——八十八星座；中国把它建成一座宫城——<span class="gx-gold">三垣二十八宿</span>，紫微垣是帝庭，太微垣是朝堂，天市垣是天街，每一宿都是一位有职司的星官。</p>
          <p>古人正是用这套坐标读时间的：看哪一宿初昏挂上中天，就知道节气走到哪里——星空，是他们刻在天上的历法。</p>
        </div>
      </section>

      <!-- 第五幕 · 定格你的宇宙快照（CTA，M-SW4 表单原样）
           [guanxing patch] M-SW40：原 ref="cta" 仅服务被删的 Hero scrollToCta，随之去除。 -->
      <section class="land-sec cta-sec reveal">
        <div class="sec-kicker">肆 · 定格</div>
        <v-card class="birth-card" elevation="12">
          <div class="cta-title gx-serif">定格你的宇宙快照</div>
          <div class="cta-sub">输入出生的日期、时间和地点，星临会显示那一刻、那个地方的天空。</div>

          <!-- [guanxing patch] M-SW23：出生日期改下拉滚轮（用户需求「选日期的换一种方式」，
               AskUserQuestion 定案），替代原生 datetime-local 控件（其样式与深色主题生硬、
               移动端体验差）；提交链路（submit）零改动。
               [guanxing patch] M-SW23b：下拉菜单默认 fixed 挂在 v-application 下且 z-index
               仅 8，被 z-index 150 的落地层全屏盖住（菜单 items 在渲染但视觉不可见）——
               修复见 style 尾部 .menuable__content__active 提权。
               （曾试 attach=".birth-overlay" 让菜单进落地层 stacking context，菜单虽可见但
               Vuetify 在内部滚动容器中的 absolute 定位错位 2000px+，故弃用 attach。）
               [guanxing patch] M-SW50：删时/分、年一行月日一行（用户指令「时 分 删掉
               年一行 月 日 一行」），修移动端五列压缩年份截断。
               [guanxing patch] M-SW51（用户指令「分三行吧 第三个 在加上 时分 提示非必填
               时(非必填) 分(非必填)」）：恢复时/分两列并定版三行布局——年独占一行、
               月/日同行、时/分同行；时/分 placeholder 内联标注「(非必填)」，
               未填按当天 00:00 计算（M-SW23b 语义恢复，canSubmit 只门控年月日）。 -->
          <!-- [guanxing patch] M-SW59（用户需求「在填写日期的上面添加一个拨钮选择
               农历和公历 默认公历」）：历法拨钮，置于日期填写区上方。公历态与原
               五列滚轮行为零差异；农历态月列显示正月~腊月（该年有闰月则多一项
               「闰X月」插在其平月之后）、日列按农历月 29/30 天动态生成，提交前由
               lunarToSolar 换算成公历再进原 birthday 管道（submit 链路零改动）。
               切换拨钮清空月/日、年保留（AskUserQuestion 定案）；时/分是绝对
               时刻不受历法影响。 -->
          <div class="cal-toggle-row">
            <span class="cal-toggle-label">历法</span>
            <v-btn-toggle v-model="calendarType" mandatory class="cal-toggle">
              <v-btn value="solar">公历</v-btn>
              <v-btn value="lunar">农历</v-btn>
            </v-btn-toggle>
          </div>
          <div class="birth-dob-row">
            <v-select v-model="birthYear" :items="yearOptions" placeholder="年"
                      :menu-props="dobMenuProps" dark dense outlined hide-details class="dob-cell dob-cell--year"></v-select>
            <v-select v-model="birthMonth" :items="monthOptions" placeholder="月"
                      :menu-props="dobMenuProps" dark dense outlined hide-details class="dob-cell dob-cell--half"></v-select>
            <v-select v-model="birthDay" :items="dayOptions" placeholder="日"
                      :menu-props="dobMenuProps" dark dense outlined hide-details class="dob-cell dob-cell--half"></v-select>
            <v-select v-model="birthHour" :items="hourOptions" placeholder="时(非必填)"
                      :menu-props="dobMenuProps" dark dense outlined hide-details class="dob-cell dob-cell--half"></v-select>
            <v-select v-model="birthMinute" :items="minuteOptions" placeholder="分(非必填)"
                      :menu-props="dobMenuProps" dark dense outlined hide-details class="dob-cell dob-cell--half"></v-select>
          </div>

          <v-text-field v-model="cityQuery" label="出生地点（搜索城市）" placeholder="如：上海 / shanghai"
                        dark dense outlined hide-details class="mt-4"
                        append-icon="mdi-magnify"></v-text-field>
          <div class="birth-city-selected" v-if="selectedCity">
            已选：{{ selectedCity.zh }}（{{ selectedCity.region }}）
          </div>
          <v-list dark dense class="birth-city-list">
            <v-list-item v-for="c in filteredCities" :key="c.en" @click.stop="pickCity(c)">
              <v-list-item-content>
                <v-list-item-title>{{ c.zh }}</v-list-item-title>
                <v-list-item-subtitle>{{ c.region }}</v-list-item-subtitle>
              </v-list-item-content>
            </v-list-item>
          </v-list>

          <!-- [guanxing patch] 不用 color="primary"（=白主题色）：Vuetify 2 对命名色
               按钮固定配白字（getTextColor 命名色默认 text--white），白底白字不可见。
               改显式白底黑字（见 .birth-submit 样式）。 -->
          <v-btn block large class="mt-4 birth-submit" :disabled="!canSubmit" @click.stop="submit">
            {{ engineReady ? '查看出生天象' : '星空加载中…' }}
          </v-btn>
        </v-card>
        <div class="birth-skip" @click.stop="$emit('skipped')">跳过，先看看现在的星空</div>
        <!-- [guanxing patch] M-SW40（用户需求「首页最下面用浅色小字加上 杜阿 duasweb.xyz」）：
             落地层最末尾署名字样。纯文本不可点（用户选「纯文本」方案），随落地层滚动到
             底才可见；色阶比 .birth-skip（0.55）更淡一级。 -->
        <div class="landing-footer">杜阿&nbsp;&nbsp;duasweb.xyz</div>
      </section>
    </div>
  </div>
</template>

<script>

import Moment from 'moment'
import { CITY_LIST } from '@/assets/city-list.js'
// [guanxing patch] M-SW59：农历换算（1900–2100 历表 + 闰月支持，零新依赖）
import { lunarMonthsOf, lunarDaysOf, lunarToSolar } from '@/assets/lunar-calendar.js'

export default {
  data: function () {
    return {
      // [guanxing patch] M-SW23：滚轮的原始选择（null = 未选）。
      // birthday 改由 computed 拼接（格式与原 datetime-local 一致），提交链路不变。
      // [guanxing patch] M-SW50 删时/分后 M-SW51 恢复（用户指令「第三个在加上时分」），
      // 未填仍按 M-SW23b 语义记当天 00:00。
      birthYear: null,
      birthMonth: null,
      birthDay: null,
      birthHour: null,
      birthMinute: null,
      // [guanxing patch] M-SW59：历法拨钮（'solar' | 'lunar'），默认公历（用户定案）。
      // 农历态下 birthMonth 存 lunarMonthsOf 的 value 编码（'5'=五月、'5l'=闰五月），
      // 换算出口见 computed solarBirthdayParts。
      calendarType: 'solar',
      // [guanxing patch] M-SW23b：日期列下拉菜单统一 contentClass——全局 M-SW12 样式
      // 使 v-list 背景透明（底栏菜单靠显式 .secondary 类才有底色），菜单内容直接
      // 透出卡片下层文字；dob-menu 给菜单补面板底色（offsetY 是 v-select 默认值，
      // 覆写 menu-props 时必须保留）。
      dobMenuProps: { offsetY: true, contentClass: 'dob-menu' },
      cityQuery: '',
      // 默认预选北京（CITY_LIST[0]），可搜索替换
      selectedCity: CITY_LIST[0]
    }
  },
  computed: {
    // 引擎就绪以 store.state.stel（replaceStelWebEngine，响应式）为准；
    // Vue.prototype.$stel 非 reactive，不能驱动 computed。
    engineReady: function () {
      return !!this.$store.state.stel
    },
    // [guanxing patch] M-SW23：滚轮候选项。年份倒序（先见近年）；日数随年月
    // 自动修正（闰年 2 月 29 天），年/月未选齐时给全量 31 供预选。
    yearOptions: function () {
      const yNow = new Date().getFullYear()
      const out = []
      for (let y = yNow; y >= 1900; y--) out.push(y)
      return out
    },
    // [guanxing patch] M-SW59：月候选项随历法拨钮切换。公历 1~12（数字，
    // 原样）；农历正月~腊月（有闰月的年份多一项「闰X月」插在其平月之后，
    // value 是 '5'/'5l' 字符串编码——月长随年而异，须先选年才列得出月份）。
    monthOptions: function () {
      if (this.calendarType === 'lunar') {
        return this.birthYear == null ? [] : lunarMonthsOf(this.birthYear)
      }
      return Array.from({ length: 12 }, (_, i) => i + 1)
    },
    dayOptions: function () {
      const y = this.birthYear
      const m = this.birthMonth
      // [guanxing patch] M-SW59：农历态日数 = 该农历月真实长度（29/30，闰月
      // 单独计）；年或月未选时给 30 供预览（农历月最长 30 天）。
      if (this.calendarType === 'lunar') {
        if (y == null || m == null) return Array.from({ length: 30 }, (_, i) => i + 1)
        const days = lunarDaysOf(y, parseInt(m, 10), this.isLeapMonthCode(m))
        return Array.from({ length: days > 0 ? days : 30 }, (_, i) => i + 1)
      }
      const days = (y != null && m != null) ? new Date(y, m, 0).getDate() : 31
      return Array.from({ length: days }, (_, i) => i + 1)
    },
    // [guanxing patch] M-SW51：时/分两列 M-SW50 删除后按用户指令恢复（非必填）
    hourOptions: function () {
      return Array.from({ length: 24 }, (_, i) => i)
    },
    minuteOptions: function () {
      return Array.from({ length: 60 }, (_, i) => i)
    },
    // [guanxing patch] M-SW23：与原 datetime-local 输入同格式（'YYYY-MM-DDTHH:mm'），
    // 年月日未选齐返回 ''（canSubmit 门控 + submit 的 hasSec 判断依赖该长度，勿改格式）。
    // [guanxing patch] M-SW23b：时/分可选（placeholder 标注非必填），未填按当天
    // 00:00 计算——canSubmit 只门控年月日。M-SW50 曾删时/分改固定 T00:00，
    // M-SW51 恢复五列（三行布局）与 16 位 YYYY-MM-DDTHH:mm 格式。
    // [guanxing patch] M-SW59：年月日不再直接取 birthYear/birthMonth/birthDay，
    // 改走 solarBirthdayParts 的换算出口——公历态原样透传，农历态先经
    // lunarToSolar 换算成公历再拼串，submit 链路（+08:00 解析、URL 同步、
    // dateText 展示）拿到的永远是公历日期，零改动。
    birthday: function () {
      const p = this.solarBirthdayParts
      if (!p) return ''
      const h = this.birthHour == null ? 0 : this.birthHour
      const mi = this.birthMinute == null ? 0 : this.birthMinute
      const pad = (n) => (n < 10 ? '0' + n : '' + n)
      return p.y + '-' + pad(p.m) + '-' + pad(p.d) + 'T' + pad(h) + ':' + pad(mi)
    },
    // [guanxing patch] M-SW59：换算出口——无论拨钮在哪档，统一产出「实际公历
    // 年月日」。农历态非法组合（超出历表域/日大于当月/闰月与该年不符）返回
    // null → birthday 为 '' → canSubmit false（提交按钮禁用即兜底）。
    // 时/分不在此处理：时刻是绝对时间，不受历法影响。
    solarBirthdayParts: function () {
      const y = this.birthYear
      const m = this.birthMonth
      const d = this.birthDay
      if (y == null || m == null || d == null) return null
      if (this.calendarType === 'lunar') {
        return lunarToSolar(y, parseInt(m, 10), d, this.isLeapMonthCode(m))
      }
      return { y: y, m: m, d: d }
    },
    // 本地城市过滤（M-SW3c 同款逻辑）：中文 / 拼音 / 英文名；空查询显示前 12 个
    filteredCities: function () {
      const q = this.cityQuery.trim().toLowerCase()
      if (!q) return CITY_LIST.slice(0, 12)
      return CITY_LIST.filter(c => c.zh.includes(q) || c.py.includes(q) || c.en.toLowerCase().includes(q))
    },
    canSubmit: function () {
      return this.birthday !== '' && !!this.selectedCity && this.engineReady
    }
  },
  watch: {
    // [guanxing patch] M-SW23：年/月变化后当月日数收缩时，把已选的日夹回合法域
    // （如先选 31 日再改 2 月 → 自动变 28/29）。
    // [guanxing patch] M-SW59：农历态换年后，已选月可能不再存在（如从有闰五月
    // 的年换到无闰月的年），先夹月再夹日。
    birthYear: function () { this.clampMonth(); this.clampDay() },
    birthMonth: function () { this.clampDay() },
    // [guanxing patch] M-SW59：切换历法清空月/日（AskUserQuestion 定案「清空月和日，
    // 年保留」）——两套历法的月日编码不同（农历月含闰月后缀编码、月长 29/30），
    // 残留选择必然非法；年两侧语义近似故保留。
    calendarType: function () {
      this.birthMonth = null
      this.birthDay = null
    },
    // [guanxing patch] M-SW21：引擎就绪后让星空以 100 倍速缓慢漂移（约 0.42°/s，
    // 深邃缓移；实时 1x 近乎静止），作落地页「星空即 Hero」的氛围底。提交路径
    // 随 submit() 置 time_speed=0 冻结、跳过路径由 App.vue onBirthSkipped 恢复 1。
    // birth=1 直达不挂载本组件，冻结语义不受影响。immediate 兼容引擎先就绪的时序。
    // [guanxing patch] M-SW21b（用户需求「这个首页默认关闭大气」）：落地页期间
    // 关闭大气（地平线辉光/雾光随引擎 fader 平滑淡出），背景星空更深邃纯净；
    // 离开落地页（提交/跳过）由 App.vue 两个回调恢复 true（出生天象与实时星空
    // 回归真实观感，fader 平滑渐显无跳变）。
    // [guanxing patch] （用户需求「首页背景星空的星座名字不显示」）：落地页期间
    // 隐藏星座名标签（连线保留）。时序上 App.vue onReady 同步设 labels_visible=true
    // 先于本 watcher（Vue watcher 在 nextTick 异步 flush），此处 false 最终生效；
    // 离开路径由 App.vue onBirthSubmitted/onBirthSkipped 恢复 true（回归
    // 「默认打开」契约，与 atmosphere 同一套落地页临时态模式）。
    engineReady: {
      handler: function (v) {
        if (v && this.$stel) {
          this.$stel.core.time_speed = 100
          this.$stel.core.atmosphere.visible = false
          this.$stel.core.constellations.labels_visible = false
        }
      },
      immediate: true
    }
  },
  mounted: function () {
    // [guanxing patch] M-SW21：滚动渐显（一次性入幕）。无 IntersectionObserver 或
    // 用户偏好减少动效时直接全亮，不做动画。
    const els = this.$el.querySelectorAll('.reveal')
    const reduced = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches
    if (reduced || !('IntersectionObserver' in window)) {
      for (const el of els) el.classList.add('is-in')
      return
    }
    this._io = new IntersectionObserver((entries) => {
      for (const en of entries) {
        if (en.isIntersecting) {
          en.target.classList.add('is-in')
          this._io.unobserve(en.target)
        }
      }
    }, { threshold: 0.15 })
    for (const el of els) this._io.observe(el)
  },
  beforeDestroy: function () {
    if (this._io) {
      this._io.disconnect()
      this._io = null
    }
  },
  methods: {
    pickCity: function (c) {
      this.selectedCity = c
      this.cityQuery = ''
    },
    // [guanxing patch] M-SW23：日数收缩时夹回已选日（配合 year/month watcher）
    clampDay: function () {
      const days = this.dayOptions.length
      if (this.birthDay != null && this.birthDay > days) this.birthDay = days
    },
    // [guanxing patch] M-SW59：农历月 value 编码 '5l' 的闰月后缀判定
    // （'l' 后缀见 assets/lunar-calendar.js lunarMonthsOf）
    isLeapMonthCode: function (m) {
      return String(m).slice(-1) === 'l'
    },
    // [guanxing patch] M-SW59：农历态下已选月在新年份不存在（如闰五月）时清空
    clampMonth: function () {
      if (this.calendarType !== 'lunar' || this.birthMonth == null) return
      if (!this.monthOptions.some((o) => o.value === this.birthMonth)) {
        this.birthMonth = null
      }
    },
    submit: function () {
      if (!this.canSubmit) return
      const c = this.selectedCity
      // datetime-local 值是 'YYYY-MM-DDTHH:mm'（或带秒）；出生地统一按中国民用时 +08:00
      // 解析（CITY_LIST 全为中国城市）。'+08:00' 明确偏移，Moment 得到正确的 UTC 时刻。
      const hasSec = this.birthday.length > 16
      const iso = this.birthday + (hasSec ? '' : ':00') + '+08:00'
      const m = Moment(iso)
      if (!m.isValid()) return

      const DD2R = Math.PI / 180
      // 冻结在出生时刻：出生天象是那一刻的天空，不随时间流动
      this.$stel.core.time_speed = 0
      this.$stel.core.observer.utc = m.toDate().getMJD()
      this.$stel.core.observer.latitude = c.lat * DD2R
      this.$stel.core.observer.longitude = c.lng * DD2R
      this.$stel.core.observer.elevation = 0
      this.$store.commit('setCurrentLocation', {
        short_name: c.zh,
        country: c.region,
        street_address: '',
        lat: c.lat,
        lng: c.lng,
        alt: 0,
        accuracy: 1
      })

      // 出生时刻的月相照亮比例（引擎 obj_info.h INFO_PHASE，0~1）。读取失败只
      // 省略月相段，不阻断展示。
      let phasePct = null
      try {
        const moon = this.$stel.getObj('NAME Moon')
        if (moon) {
          const p = moon.getInfo('PHASE', this.$stel.core.observer)
          if (isFinite(p)) phasePct = Math.round(p * 100)
        }
      } catch (e) { console.log('Moon phase unavailable: ' + e) }

      // URL 同步参数：刷新/分享可重现同一片天象。vue-router 对 query 对象做
      // encodeURIComponent（'+' → %2B），读取时自动解码。
      this.$router.replace({
        query: { date: iso, lat: String(c.lat), lng: String(c.lng), city: c.zh, birth: '1' }
      })

      this.$emit('submitted', {
        dateText: this.birthday.replace('T', ' '),
        // M-SW15：带偏移的完整 ISO 串，供本命弹窗用 astronomy-engine 计算
        // 出生时刻太阳/月亮黄经（与引擎冻结时刻同一 UTC 瞬间）。
        iso: iso,
        cityText: c.zh,
        phasePct: phasePct
      })
    }
  }
}
</script>

<style>

/* [guanxing patch] M-SW21 首页落地页：实时星空之上的五幕轻纱。
   设计语言：深空蓝黑 + 星图金唯一强调色 + 衬线标题（.gx-serif 组件内 class，
   不进 :root token——M27 断言核对 App.vue 与 solar/styles.css 双侧同名同值）。 */

/* 滚动容器：html/body/#app 均 position:fixed（App.vue 全局），页面级滚动不存在，
   必须内部滚动；z-index 自 300 降为 150——低于 v-dialog(202)/星命入口(200)，
   淡出离场期间不遮挡提交后同步弹出的本命星命弹窗。 */
.birth-overlay {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  z-index: 150;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  scroll-behavior: smooth;
}

/* 双层 scrim：纵向渐变（上下深、中间透）+ 四角 vignette，均 fixed 且不接事件，
   星空从幕间透出。 */
.landing-scrim {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  z-index: 0;
  pointer-events: none;
  background: linear-gradient(180deg,
    rgba(6, 6, 8, 0.84) 0%,
    rgba(6, 6, 8, 0.40) 24%,
    rgba(6, 6, 8, 0.34) 55%,
    rgba(6, 6, 8, 0.92) 100%);
}

.landing-vignette {
  position: fixed;
  top: 0; left: 0; right: 0; bottom: 0;
  z-index: 0;
  pointer-events: none;
  background: radial-gradient(ellipse at 50% 42%,
    rgba(6, 6, 8, 0) 38%,
    rgba(6, 6, 8, 0.45) 100%);
}

.landing-inner {
  position: relative;
  z-index: 1;
  max-width: 720px;
  margin: 0 auto;
  padding: 0 24px;
}

/* 衬线标题栈（碑刻/古籍感）——仅本组件使用 */
.gx-serif {
  font-family: Georgia, "Songti SC", "STSong", "SimSun", serif;
}

.gx-gold { color: var(--gx-gold); }

/* ---- 第一幕 · Hero ---- */
.hero {
  min-height: 100vh;
  min-height: 100svh;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 80px 0 56px;
}

/* [guanxing patch] M-SW40：Hero CTA 按钮删除后，下滚 cue 升为第 4 个子元素；
   原 cue 的 0.6s 延迟保留（改挂在 :nth-child(4)），Hero 入场节奏不变。 */
.hero > * { animation: heroIn 0.9s ease both; }
.hero > :nth-child(2) { animation-delay: 0.12s; }
.hero > :nth-child(3) { animation-delay: 0.24s; }
.hero > :nth-child(4) { animation-delay: 0.6s; }

@keyframes heroIn {
  from { opacity: 0; transform: translateY(18px); }
  to { opacity: 1; transform: none; }
}

.hero-kicker {
  font-size: 13px;
  letter-spacing: 0.9em;
  text-indent: 0.9em;
  color: var(--gx-text-dim);
  margin-bottom: 30px;
}

.hero-title {
  font-size: clamp(34px, 6vw, 56px);
  font-weight: 600;
  line-height: 1.42;
  letter-spacing: 0.06em;
  color: #f5f5f5;
  margin: 0 0 26px;
  text-shadow: 0 2px 26px rgba(0, 0, 0, 0.7);
}
/* [guanxing patch] 移动端主标三行断行：桌面隐藏该 <br>（维持两行），移动端显示（见模板注释） */
.hero-br-m { display: none; }

.hero-sub {
  font-size: 15px;
  line-height: 2.0;
  color: var(--gx-text-dim);
  max-width: 540px;
  margin: 0 auto 40px;
}

.hero-scrollcue {
  width: 1px;
  height: 52px;
  margin-top: 46px;
  background: linear-gradient(180deg, rgba(242, 242, 242, 0.55), rgba(242, 242, 242, 0));
  animation: cueDrift 2.4s ease-in-out infinite;
}

@keyframes cueDrift {
  0%, 100% { transform: translateY(0); opacity: 0.55; }
  50% { transform: translateY(8px); opacity: 1; }
}

/* ---- 二至五幕通用 ---- */
.land-sec { padding: clamp(120px, 21vh, 240px) 0; }

.sec-kicker {
  color: var(--gx-gold);
  font-size: 13px;
  letter-spacing: 0.46em;
  margin-bottom: 26px;
}

.sec-prose p {
  font-size: 15.5px;
  line-height: 2.1;
  color: rgba(242, 242, 242, 0.85);
  margin: 0 0 20px;
}

/* ---- 第四幕 · 三问（原生 details） ---- */
.faq details { border-top: 1px solid var(--gx-panel-border); }
.faq details:last-of-type { border-bottom: 1px solid var(--gx-panel-border); }

.faq summary {
  list-style: none;
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 21px 4px;
  cursor: pointer;
  font-size: 15.5px;
  color: #f2f2f2;
}

.faq summary::-webkit-details-marker { display: none; }

.faq-no { color: var(--gx-gold); font-size: 16px; }

.faq summary::after {
  content: '+';
  margin-left: auto;
  color: var(--gx-gold);
  font-size: 18px;
  transition: transform 0.25s ease;
}

.faq details[open] summary::after { transform: rotate(45deg); }

.faq-body {
  padding: 0 4px 24px;
  font-size: 14px;
  line-height: 2.0;
  color: var(--gx-text-dim);
}

.faq details[open] .faq-body { animation: faqIn 0.35s ease; }

@keyframes faqIn {
  from { opacity: 0; transform: translateY(-6px); }
  to { opacity: 1; transform: none; }
}

/* ---- 第五幕 · CTA ---- */
.cta-sec { padding-bottom: 40px; }

.birth-card {
  width: 100%;
  max-width: 560px;
  margin: 0 auto;
  padding: 30px 30px 22px;
  background: var(--gx-panel-bg)!important;
  border: 1px solid var(--gx-panel-border)!important;
  border-radius: var(--gx-radius-panel)!important;
  box-shadow: var(--gx-shadow);
}

.cta-title {
  font-size: 22px;
  font-weight: 600;
  color: #fff;
  margin-bottom: 8px;
}

.cta-sub {
  font-size: 13px;
  color: rgba(255, 255, 255, 0.6);
  margin-bottom: 20px;
  line-height: 1.6;
}

/* [guanxing patch] M-SW59：历法拨钮。v-btn-toggle 激活态默认染 primary--text
   （本主题 primary=白），白字落深底不可读（M-SW44 同源教训），显式定色：
   未激活白 0.65、激活白底近黑字（与 .birth-submit 同一强调语言）。
   .v-application 前缀提特异性压主题注入样式。 */
.cal-toggle-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}
.cal-toggle-label {
  font-size: 12px;
  letter-spacing: 0.08em;
  color: rgba(255, 255, 255, 0.6);
}
.v-application .cal-toggle {
  background: rgba(255, 255, 255, 0.04)!important;
  border: 1px solid var(--gx-panel-border);
  border-radius: 6px;
  overflow: hidden;
}
.v-application .cal-toggle .v-btn {
  color: rgba(255, 255, 255, 0.65)!important;
  background: transparent!important;
}
.v-application .cal-toggle .v-btn.v-btn--active {
  background: #ffffff!important;
  color: #0a0a0c!important;
}

/* [guanxing patch] M-SW23：日期滚轮列。M-SW50 定版「年独占一行」，M-SW51 定版
   三行：年 / 月·日 / 时·分——月/日/时/分各 flex-basis calc(50% - 4px)（gap 8px 下
   两列恰满一行），移动端窄屏不再五列压缩折行截断年份。 */
.birth-dob-row {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}
.birth-dob-row .dob-cell {
  flex: 1 1 0;
  min-width: 62px;
}
.birth-dob-row .dob-cell--year {
  flex: 1 1 100%;
}
.birth-dob-row .dob-cell--half {
  flex: 1 1 calc(50% - 4px);
}
/* 滚轮列居中显示选中值，placeholder（年/月/日/时/分）弱化为提示色。
   非 scoped 样式：直接用后代选择器（>>> 深度语法仅 scoped 有效）。 */
.birth-dob-row .dob-cell .v-select__selection {
  width: 100%;
  text-align: center;
  justify-content: center;
}
.birth-dob-row .dob-cell .v-label,
.birth-dob-row .dob-cell input::placeholder {
  width: 100%;
  text-align: center;
  color: rgba(255, 255, 255, 0.45);
}
.birth-city-selected {
  font-size: 12px;
  color: var(--gx-blue);
  margin: 6px 0;
}

/* [guanxing patch] 提交按钮显式配色：enabled 白底 + 近黑字（强调态）。
   根因：Vuetify 2 对命名色按钮（color="primary"=白）固定配白字
   （getTextColor 命名色默认 text--white）→ 白底白字不可见。
   disabled 态不写覆盖：Vuetify dark 默认（rgba(255,255,255,.12) 底 + .3 白字）
   是深底淡字的正确弱化态，且其 4 类 + important 压得过本规则（1 类），
   保留默认即可。 */
.birth-submit {
  background: #ffffff!important;
  color: #0a0a0c!important;
}
.birth-submit .v-btn__content { color: inherit; }

.birth-city-list {
  max-height: 200px;
  overflow-y: auto;
  background: transparent!important;
}

/* [guanxing patch] M-SW40：尾部空白从 .birth-skip（原 padding-bottom 64px）
   移到新署名下，避免「跳过」与署名之间拉出双倍空隙。 */
.birth-skip {
  text-align: center;
  padding: 48px 0 24px;
  font-size: 13px;
  color: rgba(255, 255, 255, 0.55);
  cursor: pointer;
}

.birth-skip:hover { color: #fff; }

/* [guanxing patch] M-SW40：首页最底部署名字样。浅色小字——比 .birth-skip
   的 0.55 再淡一级（0.38），12px + 宽字距，纯文本不可点（用户选「纯文本」方案）。 */
.landing-footer {
  text-align: center;
  padding: 0 0 52px;
  font-size: 12px;
  letter-spacing: 0.14em;
  color: rgba(255, 255, 255, 0.38);
  user-select: none;
}

/* ---- 滚动渐显（IntersectionObserver 加 is-in，一次性） ---- */
.reveal {
  opacity: 0;
  transform: translateY(26px);
  transition: opacity 0.8s ease, transform 0.8s ease;
}

.reveal.is-in {
  opacity: 1;
  transform: none;
}

/* ---- 动效克制：reduced-motion 全静止 ---- */
@media (prefers-reduced-motion: reduce) {
  .birth-overlay { scroll-behavior: auto; }
  .hero > *, .hero-scrollcue, .faq details[open] .faq-body { animation: none!important; }
  .reveal { opacity: 1!important; transform: none!important; transition: none!important; }
}

/* ---- 移动端 ---- */
/* [guanxing patch] M-SW33：手机判定放宽为「短边≤600 且触屏」，手机横屏
   （宽>600px，如 844×390）同样套用落地页紧凑版式。字面量须与
   assets/phone-media.js 同步。 */
@media (max-width: 600px), (pointer: coarse) and (max-height: 600px) and (max-width: 1100px) {
  .landing-inner { padding: 0 20px; }
  .hero-title { letter-spacing: 0.03em; }
  .hero-br-m { display: inline; }
  .birth-card { padding: 24px 18px 16px; }
  .birth-city-list { max-height: 160px; }
  .sec-prose p { font-size: 14.5px; }
}

/* [guanxing patch] M-SW23b：v-menu__content 默认 fixed 挂 v-application 下且
   z-index 仅 8，被 z-index 150 的落地层全屏盖住（日期下拉菜单 items 在渲染
   但视觉不可见）——仅激活态提权到 210（本项目 v-dialog 202 内无 v-menu，
   无冲突场景；曾试 attach=".birth-overlay"，菜单虽进落地层 stacking context
   可见，但 Vuetify 在内部滚动容器中 absolute 定位错位 2000px+，故弃用）。
   激活态类是 menuable__content__active（Vuetify 2 无 v-menu__content--active
   修饰类，实测 className：v-menu__content theme--dark v-menu__content--fixed
   menuable__content__active）。 */
.menuable__content__active { z-index: 210 !important; }

/* [guanxing patch] M-SW23b：日期列下拉菜单不透明打底。M-SW12 全局规则把
   .v-menu__content 强制 transparent(!important)、底色给内层 .v-list
   （panel-bg 0.88 半透明）——落地页菜单下方是卡片白字，12% 透出仍可读
   （实测透字）。dob-menu 内容层用不透明等效色打底（panel-bg 0.88 叠深黑
   scrim ≈ rgb(9,9,11)），列表 token 底叠其上彻底不透字；三段 specificity
   + !important 压过全局透明规则（该规则两段）。 */
.v-application .v-menu__content.dob-menu {
  background-color: rgb(9, 9, 11) !important;
  border-radius: var(--gx-radius-panel) !important;
}

</style>
